import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  exploreService,
  ExploreRoom,
  ExplorePeek,
  ExploreUpcomingEvent,
  RankedRoom,
  RankedRoomsResult,
} from '../../services/api/exploreService';
import { Room, RoomCategory, ROOM_CATEGORY_TABS } from '../../types/user';
import { Character } from '@gamedate/core-character/character';
import { useRooms } from '../../context/RoomContext';
import { useUser } from '../../context/UserContext';
import { useCharacter } from '../../context/CharacterContext';
import { CreateRoomModal } from '../CreateRoomModal';
import { PasswordPromptModal } from '../PasswordPromptModal';
import { PendingApprovalModal } from '../PendingApprovalModal';
import { CharacterSelectionModal } from '../CharacterSelectionModal';
import { PeekSheet, PeekTarget } from './PeekSheet';
import { RoomStoryCard } from '../RoomStoryCard';
import { EventRail } from '../EventRail';
import { splitNewsSections, emptyRoomInvitation } from '../../utils/roomStory';
import { CompassIcon, SearchIcon } from '../icons';
import { appMode } from '../../config';
import { isQaRoom } from '../../utils/qaRoom';
import './ExplorePage.css';

interface ExplorePageProps {
  /** Post-join hand-off (same contract RoomsDashboard receives). */
  onJoinRoom: (room: Room) => void;
}

const GAME_KINDS: { value: string; label: string; icon: string }[] = [
  { value: 'party', label: 'Party', icon: '🎉' },
  { value: 'hunt', label: 'Hunt', icon: '🔍' },
  { value: 'maze', label: 'Maze', icon: '🌀' },
  { value: 'escape', label: 'Escape', icon: '🗝️' },
  { value: 'race', label: 'Race', icon: '🏁' },
  { value: 'quiz', label: 'Quiz', icon: '❓' },
  { value: 'rts', label: 'Strategy', icon: '⚔️' },
];

export function ExplorePage({ onJoinRoom }: ExplorePageProps) {
  const { rooms, joinRoom, requestToJoin, refreshRooms, isConnected } = useRooms();
  const { user } = useUser();
  const { characters, setActiveCharacter } = useCharacter();

  // Search + filter state
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<RoomCategory>('all');
  const [activeGameKind, setActiveGameKind] = useState<string | null>(null);

  // Data
  const [exploreRooms, setExploreRooms] = useState<ExploreRoom[]>([]);
  const [loading, setLoading] = useState(true);

  // News-front-page composition (V2 Amethyst WU-03) — only when nothing is
  // filtered; a search/category/kind pick collapses back to a plain filtered rail.
  const isDefaultView = activeCategory === 'all' && !activeGameKind && !debouncedQuery;
  const [ranked, setRanked] = useState<RankedRoomsResult | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<ExploreUpcomingEvent[]>([]);

  // Peek (observe) sheet
  const [peekTarget, setPeekTarget] = useState<PeekTarget | null>(null);
  const [peek, setPeek] = useState<ExplorePeek | null>(null);
  const [peekLoading, setPeekLoading] = useState(false);

  // Surprise-me rolling animation
  const [rolling, setRolling] = useState(false);

  // Join flow (mirrors RoomsDashboard — password/character/approval gating)
  const [joiningRoomId, setJoiningRoomId] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [passwordPromptRoom, setPasswordPromptRoom] = useState<Room | null>(null);
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [pendingApprovalRoom, setPendingApprovalRoom] = useState<Room | null>(null);
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [characterSelectRoom, setCharacterSelectRoom] = useState<Room | null>(null);

  // Create-room modal (opened from a world card / surprise template / event)
  const [createTemplateId, setCreateTemplateId] = useState<string | null>(null);

  const isClassroom = appMode === 'classroom';
  const categoryTabs = isClassroom
    ? ROOM_CATEGORY_TABS.filter((t) => t.value !== 'dating')
    : ROOM_CATEGORY_TABS;

  // Re-render every 30s so time-derived UI (event "starting soon" chip, "started
  // Xm ago" bylines) stays fresh without a full data refetch.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30000);
    return () => window.clearInterval(id);
  }, []);

  // Debounce the search box (300ms).
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(id);
  }, [query]);

  // Reset the game-kind sub-filter when leaving the Games category.
  useEffect(() => {
    if (activeCategory !== 'games') setActiveGameKind(null);
  }, [activeCategory]);

  // Fetch rooms whenever the query/filters change. Backend already omits QA
  // rooms by default (belt-and-suspenders filter client-side too).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {
      search: debouncedQuery || undefined,
      category: activeCategory,
      gameKind: activeGameKind || undefined,
    };
    exploreService
      .getExploreRooms(params)
      .then((rms) => {
        if (cancelled) return;
        setExploreRooms(rms.filter((r) => !isQaRoom(r.name)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, activeCategory, activeGameKind]);

  // Background occupancy poll: a room's occupant count only reaches this
  // client via SignalR if it's already joined that room's group (RoomHub only
  // broadcasts PlayerJoinedRoom/PlayerLeftRoom to `room:{roomId}`, not to
  // every connected client), so a card for someone else's room otherwise never
  // learns about a join/leave — the effect above only re-fires on a filter
  // change. This refetches silently (no `setLoading`) so cards catch up
  // without a loading-skeleton flash or requiring a remount.
  useEffect(() => {
    if (!isConnected) return;
    const params = {
      search: debouncedQuery || undefined,
      category: activeCategory,
      gameKind: activeGameKind || undefined,
    };
    const id = window.setInterval(() => {
      exploreService.getExploreRooms(params).then((rms) => {
        setExploreRooms(rms.filter((r) => !isQaRoom(r.name)));
      });
    }, 8000);
    return () => window.clearInterval(id);
  }, [isConnected, debouncedQuery, activeCategory, activeGameKind]);

  // WU-05 ranking seam (R4): only meaningful for the unfiltered front page —
  // real relevance when the reco surface is deployed, occupancy-sort fallback otherwise.
  useEffect(() => {
    if (!isDefaultView || exploreRooms.length === 0) { setRanked(null); return; }
    let cancelled = false;
    exploreService.getRankedRooms(exploreRooms).then((res) => {
      if (!cancelled) setRanked(res);
    });
    return () => { cancelled = true; };
  }, [isDefaultView, exploreRooms]);

  useEffect(() => {
    if (!isConnected) return;
    let cancelled = false;
    exploreService.getUpcomingEvents({ scope: isClassroom ? 'classroom' : 'dating', withinHours: 24 }).then((evts) => {
      if (!cancelled) setUpcomingEvents(evts);
    });
    return () => { cancelled = true; };
  }, [isConnected, isClassroom]);

  const sections = useMemo(() => {
    if (!isDefaultView) return { hero: null, liveNow: [] as RankedRoom[], forYou: [] as RankedRoom[] };
    const source: RankedRoom[] = ranked?.rooms ?? exploreRooms.map((r) => ({ ...r, relevanceReasons: [] }));
    return splitNewsSections(source, { liveNowSize: 4, forYouSize: 4 });
  }, [isDefaultView, ranked, exploreRooms]);

  // The rest of the catalog once Hero/Live-now/For-you have claimed their rooms
  // — the "Browse all" classifieds tail (default view only).
  const browseAll = useMemo(() => {
    if (!isDefaultView) return [];
    const shown = new Set(
      [sections.hero, ...sections.liveNow, ...sections.forYou]
        .filter((r): r is RankedRoom => r !== null)
        .map((r) => r.lobbyId),
    );
    return exploreRooms.filter((r) => !shown.has(r.lobbyId));
  }, [isDefaultView, sections, exploreRooms]);

  // Filtered (non-default) view honesty: a search/category pick's "Live now"
  // rail never surfaces an empty room, same R7 rule as the front page.
  const filteredLiveRooms = useMemo(() => exploreRooms.filter((r) => r.humanCount > 0), [exploreRooms]);

  const hostNameFor = useCallback(
    (lobbyId: string): string | null => rooms.find((r) => r.id === lobbyId)?.hostName ?? null,
    [rooms],
  );

  // ---- Join flow (self-contained; reuses RoomContext + the shared modals) ----

  // Resolve a live SignalR Room for an explore DTO so the real join path (with
  // its full room object for the game view) runs unchanged.
  const resolveRoom = useCallback(
    (r: ExploreRoom): Room | undefined =>
      rooms.find((rm) => rm.id === r.lobbyId || (!!r.joinCode && rm.code === r.joinCode)),
    [rooms],
  );

  const performJoin = useCallback(
    async (room: Room, password?: string, character?: Character) => {
      if (!user) return;

      if (room.hasPassword && !password) {
        setPasswordPromptRoom(room);
        setPasswordError(undefined);
        return;
      }
      if (characters.length > 1 && !character && !characterSelectRoom) {
        setCharacterSelectRoom(room);
        return;
      }

      setJoiningRoomId(room.id);
      setJoinError(null);
      try {
        const charToUse = character || characters.find((c) => c.isActive) || characters[0];
        if (charToUse && !charToUse.isActive) {
          try {
            await setActiveCharacter(charToUse.id);
          } catch (activateErr) {
            console.warn('Failed to activate character before join:', activateErr);
          }
        }

        let characterType: string;
        let gender: string;
        let appearanceData: string | undefined;
        if (charToUse) {
          const genderSuffix = charToUse.gender === 'female' ? 'Girl' : 'Boy';
          characterType = `${charToUse.characterClass}_${genderSuffix}`;
          gender = charToUse.gender;
          appearanceData = charToUse.appearanceData;
        } else {
          const genderSuffix = user.gender === 'female' ? 'Girl' : 'Boy';
          characterType = `${user.characterClass}_${genderSuffix}`;
          gender = user.gender || 'male';
        }

        const success = await joinRoom(
          room.id,
          user.id,
          user.displayName,
          user.avatar,
          characterType,
          gender,
          password,
          appearanceData,
        );
        if (success) {
          setPasswordPromptRoom(null);
          setCharacterSelectRoom(null);
          setPeekTarget(null);
          onJoinRoom(room);
        } else {
          setJoinError('Failed to join room. Please try again.');
        }
      } catch (e: unknown) {
        const err = e as Error;
        if (err.message === 'PASSWORD_REQUIRED') {
          setPasswordPromptRoom(room);
          setPasswordError(undefined);
        } else if (err.message === 'INVALID_PASSWORD') {
          setPasswordError('INVALID_PASSWORD');
        } else if (err.message === 'APPROVAL_REQUIRED') {
          try {
            const request = await requestToJoin(room.id, user.id, user.displayName, user.avatar);
            setPendingRequestId(request.id);
            setPendingApprovalRoom(room);
          } catch (requestErr) {
            console.error('Failed to send join request:', requestErr);
            setJoinError('Failed to request to join. Please try again.');
          }
        } else {
          console.error('Failed to join room:', e);
          setJoinError('Failed to join room. Please try again.');
          setPasswordPromptRoom(null);
        }
      } finally {
        setJoiningRoomId(null);
      }
    },
    [user, characters, characterSelectRoom, setActiveCharacter, joinRoom, requestToJoin, onJoinRoom],
  );

  // Entry point from Explore cards/peek/surprise: map the DTO to a live Room,
  // refreshing once if it isn't in the cached list yet.
  const joinExploreRoom = useCallback(
    async (r: ExploreRoom) => {
      let room = resolveRoom(r);
      if (!room) {
        await refreshRooms();
        room = rooms.find((rm) => rm.id === r.lobbyId || (!!r.joinCode && rm.code === r.joinCode));
      }
      if (!room) {
        setJoinError('That room just slipped away — try another or refresh.');
        setPeekTarget(null);
        return;
      }
      performJoin(room);
    },
    [resolveRoom, refreshRooms, rooms, performJoin],
  );

  const openWorld = useCallback((templateId: string) => {
    setPeekTarget(null);
    setCreateTemplateId(templateId);
  }, []);

  // Tonight rail: an event only references a template, not a live lobby — before
  // start there is nothing to join (no reservation logic, per spec); at/after
  // start, prefer an already-live room on that template, else open it fresh.
  const openEventTemplate = useCallback(
    (event: ExploreUpcomingEvent) => {
      if (!event.sceneTemplateId) return;
      const liveMatch = rooms.find((r) => r.sceneTemplateId === event.sceneTemplateId && !isQaRoom(r.name));
      if (liveMatch) {
        performJoin(liveMatch);
        return;
      }
      openWorld(event.sceneTemplateId);
    },
    [rooms, performJoin, openWorld],
  );

  // ---- Peek (observe) ----
  const openPeek = useCallback((target: PeekTarget) => {
    setPeekTarget(target);
    setPeek(null);
    if (target.kind === 'room') {
      setPeekLoading(true);
      exploreService
        .peekRoom(target.room.lobbyId)
        .then((data) => setPeek(data))
        .finally(() => setPeekLoading(false));
    }
  }, []);

  // ---- Surprise me ----
  const rollTimer = useRef<number | null>(null);
  const surpriseMe = useCallback(() => {
    if (rolling) return;
    setRolling(true);
    setJoinError(null);
    const started = Date.now();
    exploreService.surpriseMe(activeCategory).then((s) => {
      // Keep the dice tumbling for at least 600ms for the animation to read.
      const wait = Math.max(0, 600 - (Date.now() - started));
      rollTimer.current = window.setTimeout(() => {
        setRolling(false);
        if (s.type === 'room' && s.room) {
          joinExploreRoom(s.room);
        } else if (s.type === 'template' && s.template) {
          openWorld(s.template.id);
        } else {
          setJoinError("Nothing to surprise you with yet — be the first to make some mischief.");
        }
      }, wait);
    });
  }, [rolling, activeCategory, joinExploreRoom, openWorld]);

  useEffect(() => {
    return () => {
      if (rollTimer.current) window.clearTimeout(rollTimer.current);
    };
  }, []);

  const handleCharacterSelected = (character: Character) => {
    if (characterSelectRoom) performJoin(characterSelectRoom, undefined, character);
  };
  const handlePasswordSubmit = (password: string) => {
    if (passwordPromptRoom) performJoin(passwordPromptRoom, password);
  };

  return (
    <div className="explore-page">
      {/* Hero: search + surprise */}
      <div className="explore-hero">
        <div className="explore-hero-head">
          <CompassIcon size={26} className="explore-hero-icon" />
          <div>
            <h1 className="explore-title">Explore</h1>
            <p className="explore-subtitle">Wander in, peek around, find your people.</p>
          </div>
        </div>
        <div className="explore-hero-controls">
          <div className="explore-search">
            <SearchIcon size={18} className="explore-search-icon" />
            <input
              type="text"
              className="explore-search-input"
              placeholder="Search worlds & rooms…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search worlds and rooms"
            />
          </div>
          <button
            className={`explore-surprise ${rolling ? 'rolling' : ''}`}
            onClick={surpriseMe}
            disabled={rolling}
          >
            <span className="explore-dice" aria-hidden="true">🎲</span>
            {rolling ? 'Rolling…' : 'Surprise me'}
          </button>
        </div>
      </div>

      {joinError && (
        <div className="explore-error">
          <span>{joinError}</span>
          <button onClick={() => setJoinError(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Category chip rail */}
      <div className="explore-chip-rail" role="tablist" aria-label="Categories">
        {categoryTabs.map((tab) => (
          <button
            key={tab.value}
            role="tab"
            aria-selected={activeCategory === tab.value}
            className={`explore-chip ${activeCategory === tab.value ? 'active' : ''}`}
            onClick={() => setActiveCategory(tab.value)}
            title={tab.description}
          >
            <span className="explore-chip-icon">{tab.icon}</span>
            <span className="explore-chip-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Game-kind sub-rail (Games only) */}
      {activeCategory === 'games' && (
        <div className="explore-chip-rail secondary" role="tablist" aria-label="Game kinds">
          <button
            className={`explore-chip kind ${!activeGameKind ? 'active' : ''}`}
            onClick={() => setActiveGameKind(null)}
          >
            <span className="explore-chip-label">All games</span>
          </button>
          {GAME_KINDS.map((k) => (
            <button
              key={k.value}
              className={`explore-chip kind ${activeGameKind === k.value ? 'active' : ''}`}
              onClick={() => setActiveGameKind((cur) => (cur === k.value ? null : k.value))}
            >
              <span className="explore-chip-icon">{k.icon}</span>
              <span className="explore-chip-label">{k.label}</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <section className="explore-section">
          <h2 className="explore-section-title">
            <span className="explore-live-dot" aria-hidden="true" /> Live now
          </h2>
          <div className="explore-cards-rail">
            {[0, 1, 2].map((i) => (
              <div key={i} className="explore-live-card skeleton" />
            ))}
          </div>
        </section>
      ) : isDefaultView ? (
        <>
          {sections.hero && (
            <section className="explore-section">
              <RoomStoryCard
                variant="hero"
                room={sections.hero}
                isClassroom={isClassroom}
                hostName={hostNameFor(sections.hero.lobbyId)}
                onPeek={() => openPeek({ kind: 'room', room: sections.hero as ExploreRoom })}
                onJoin={() => joinExploreRoom(sections.hero as ExploreRoom)}
                joining={joiningRoomId === sections.hero.lobbyId}
              />
            </section>
          )}

          {sections.liveNow.length > 0 && (
            <section className="explore-section">
              <h2 className="explore-section-title">
                <span className="explore-live-dot" aria-hidden="true" /> Live now
              </h2>
              <div className="explore-cards-rail">
                {sections.liveNow.map((r) => (
                  <RoomStoryCard
                    key={r.lobbyId}
                    variant="medium"
                    room={r}
                    isClassroom={isClassroom}
                    hostName={hostNameFor(r.lobbyId)}
                    onPeek={() => openPeek({ kind: 'room', room: r })}
                  />
                ))}
              </div>
            </section>
          )}

          {upcomingEvents.length > 0 && (
            <section className="explore-section">
              <h2 className="explore-section-title">✨ Tonight</h2>
              <EventRail events={upcomingEvents} onOpen={openEventTemplate} />
            </section>
          )}

          {sections.forYou.length > 0 && (
            <section className="explore-section">
              <h2 className="explore-section-title">💌 For you</h2>
              <div className="explore-cards-rail">
                {sections.forYou.map((r) => (
                  <RoomStoryCard
                    key={r.lobbyId}
                    variant="medium"
                    room={r}
                    isClassroom={isClassroom}
                    hostName={hostNameFor(r.lobbyId)}
                    invitation={emptyRoomInvitation(r)}
                    onPeek={() => openPeek({ kind: 'room', room: r })}
                  />
                ))}
              </div>
            </section>
          )}

          {browseAll.length > 0 && (
            <section className="explore-section">
              <h2 className="explore-section-title">🗂️ Browse all</h2>
              <div className="explore-cards-list">
                {browseAll.map((r) => (
                  <RoomStoryCard
                    key={r.lobbyId}
                    variant="row"
                    room={r}
                    isClassroom={isClassroom}
                    hostName={hostNameFor(r.lobbyId)}
                    onPeek={() => openPeek({ kind: 'room', room: r })}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      ) : (
        <section className="explore-section">
          <h2 className="explore-section-title">
            <span className="explore-live-dot" aria-hidden="true" /> Live now
          </h2>
          {filteredLiveRooms.length > 0 ? (
            <div className="explore-cards-rail">
              {filteredLiveRooms.map((r) => (
                <RoomStoryCard
                  key={r.lobbyId}
                  variant="medium"
                  room={r}
                  isClassroom={isClassroom}
                  hostName={hostNameFor(r.lobbyId)}
                  onPeek={() => openPeek({ kind: 'room', room: r })}
                />
              ))}
            </div>
          ) : (
            <p className="explore-empty">Nobody's here yet — be the mystery.</p>
          )}
        </section>
      )}

      {/* Peek (observe) sheet */}
      {peekTarget && (
        <PeekSheet
          target={peekTarget}
          peek={peek}
          peekLoading={peekLoading}
          joining={joiningRoomId !== null}
          hostName={peekTarget.kind === 'room' ? hostNameFor(peekTarget.room.lobbyId) : null}
          onClose={() => setPeekTarget(null)}
          onJoin={joinExploreRoom}
          onOpenWorld={(t) => openWorld(t.id)}
        />
      )}

      {/* Create-room modal preselected to the chosen world */}
      {createTemplateId !== null && (
        <CreateRoomModal
          initialSceneTemplateId={createTemplateId}
          onClose={() => setCreateTemplateId(null)}
        />
      )}

      {/* Shared join-gating modals (same components RoomsDashboard uses) */}
      {passwordPromptRoom && (
        <PasswordPromptModal
          roomName={passwordPromptRoom.name}
          onSubmit={handlePasswordSubmit}
          onCancel={() => {
            setPasswordPromptRoom(null);
            setPasswordError(undefined);
          }}
          error={passwordError}
          isSubmitting={joiningRoomId === passwordPromptRoom.id}
        />
      )}

      {pendingApprovalRoom && pendingRequestId && (
        <PendingApprovalModal
          room={pendingApprovalRoom}
          requestId={pendingRequestId}
          onCancel={() => {
            setPendingApprovalRoom(null);
            setPendingRequestId(null);
          }}
          onApproved={(room) => {
            setPendingApprovalRoom(null);
            setPendingRequestId(null);
            onJoinRoom(room);
          }}
          onDenied={(reason) => {
            setPendingApprovalRoom(null);
            setPendingRequestId(null);
            setJoinError(reason ? `Request denied: ${reason}` : 'Your request to join was denied.');
          }}
        />
      )}

      {characterSelectRoom && characters.length > 1 && (
        <CharacterSelectionModal
          characters={characters}
          onSelect={handleCharacterSelected}
          onCancel={() => setCharacterSelectRoom(null)}
          isLoading={joiningRoomId === characterSelectRoom.id}
        />
      )}

      {!isConnected && (
        <div className="explore-offline">Reconnecting to the server…</div>
      )}
    </div>
  );
}
